import { makeObservable, observable, action } from "mobx";
import { v4 as uuidv4 } from "uuid";

// Class Rule to represent an individual rule
export class Rule {
  sourceData = "";
  sourceField = "";
  operator = "";
  targetType = "";
  targetValue = "";
  targetData = "";

  constructor() {
    makeObservable(this, {
      sourceData: observable,
      sourceField: observable,
      operator: observable,
      targetType: observable,
      targetValue: observable,
      targetData: observable,
      updateField: action,
    });
  }

  // Method to update a specific field of the rule
  updateField(field, value) {
    this[field] = value;
  }
}

// Class RulesStore to manage the state of the rules
class RulesStore {
  fields = [];
  operator = "";
  message = "";
  rulesGroups = {};
  showRightPreview = false;
  isError = false;
  errorMessage = "";

  constructor() {
    makeObservable(this, {
      operator: observable,
      message: observable,
      rulesGroups: observable,
      fields: observable,
      showRightPreview: observable,
      isError: observable,
      setOperator: action,
      updateRule: action,
      removeRule: action,
      loadExistingRules: action,
      clear: action,
      addGroup: action,
      addRuleToGroup: action,
      removeGroup: action,
      saveRules: action,
      setShowRightPreview: action,
      getRule: action,
    });
  }

  // Method to set whether to show the preview
  setShowRightPreview(show = false) {
    this.showRightPreview = show;
  }

  // Method to load existing rules
  loadExistingRules(rules) {
    if (!rules.length) return this.addGroup("AND");
    for (const rule of rules) {
      const newGroupId = uuidv4();
      this.rulesGroups[newGroupId] = rule;
      this.operator = rule.logical_operator;
    }
  }

  // Method to set the available fields
  setFields(fields) {
    this.fields = fields;
  }

  // Method to set the logical operator
  setOperator(operator) {
    this.operator = operator;
  }

  // Method to get a specific rule
  getRule(groupId, index) {
    if (!groupId || !this.rulesGroups[groupId]) {
      this.addGroup("AND");
      groupId = Object.keys(this.rulesGroups)[0];
    }

    const group = this.rulesGroups[groupId];

    if (!group.conditions) {
      group.conditions = [];
    }

    if (typeof index === "undefined" || index === null || index < 0) {
      return null;
    }

    if (!group.conditions[index]) {
      const newRule = new Rule();
      newRule.sourceData = "";
      newRule.sourceField = "";
      newRule.operator = "";
      newRule.targetType = "";
      newRule.targetValue = "";
      newRule.targetData = "";
      newRule.fieldType = "";

      group.conditions[index] = newRule;
      return newRule;
    }

    if (!(group.conditions[index] instanceof Rule)) {
      const ruleData = group.conditions[index];
      const newRule = new Rule();

      const propertiesToCopy = [
        "sourceData",
        "sourceField",
        "operator",
        "targetType",
        "targetValue",
        "targetData",
        "fieldType",
      ];

      propertiesToCopy.forEach((prop) => {
        if (ruleData.hasOwnProperty(prop)) {
          newRule[prop] = ruleData[prop];
        }
      });

      group.conditions[index] = newRule;
      return newRule;
    }

    return group.conditions[index];
  }

  // Method to add a new group of rules
  addGroup(operator) {
    const newGroupId = uuidv4();
    this.rulesGroups[newGroupId] = {
      logical_operator: operator,
      type: "GROUP",
      conditions: [],
    };
    return newGroupId;
  }

  // Method to remove a group of rules
  removeGroup(groupId) {
    delete this.rulesGroups[groupId];
  }

  // Method to add a new rule to a group
  addRuleToGroup(groupId) {
    if (!this.rulesGroups[groupId]) {
      this.addGroup("AND");
      groupId = Object.keys(this.rulesGroups)[0];
    }
    this.rulesGroups[groupId].conditions.push(new Rule()); // Adds a new Rule instance
  }

  // Method to update a specific rule
  updateRule(index, data, groupdId) {
    const rule = this.rulesGroups[groupdId]?.conditions[index];
    if (rule instanceof Rule) {
      Object.assign(rule, {
        ...rule,
        fieldType: data.fieldType || "STRING",
      });
      return rule;
    } else {
      this.isError = true;
      return null;
    }
  }

  // Method to remove a specific rule
  removeRule(index, groupdId) {
    return (this.rulesGroups[groupdId].conditions = [
      ...this.rulesGroups[groupdId].conditions.filter((_, id) => id !== index),
    ]);
  }

  // Method to save the rules
  saveRules() {
    return Object.values(this.rulesGroups).map((group) => ({
      logical_operator: group.logical_operator,
      type: "GROUP",
      conditions: group.conditions.map((condition) => ({
        type: "CONDITION",
        source_data: condition.sourceData,
        source_field: condition.sourceField,
        field_type: condition.fieldType,
        operator: condition.operator,
        target_type: condition.targetType,
        target_data: condition.targetData,
        target_value: condition.targetValue,
      })),
    }));
  }

  // Method to clear the state of the rules
  clear() {
    this.fields = [];
    this.operator = "";
    this.message = "";
    this.rulesGroups = {};
    this.message = "";
    this.isError = false;
  }

  loadFilters(filters) {
    this.isError = false;
    try {
      this.clear();

      if (!filters || !Array.isArray(filters)) {
        return;
      }

      filters.forEach((mainGroup) => {
        if (!mainGroup || typeof mainGroup !== 'object') {
          return;
        }

        const groupId = this.addGroup(mainGroup.logical_operator || "OR");

        if (mainGroup.conditions && Array.isArray(mainGroup.conditions)) {
          mainGroup.conditions.forEach((condition) => {
            if (!condition || typeof condition !== 'object') {
              return;
            }

            const rule = new Rule();

            rule.sourceData = condition.source_data || "";
            rule.sourceField = condition.source_field || "";
            rule.fieldType = condition.field_type || "";
            rule.operator = condition.operator || "";
            rule.targetType = condition.target_type || "";
            rule.targetData = condition.target_data || "";
            rule.targetValue = condition.target_value || "";

            this.rulesGroups[groupId].conditions.push(rule);
          });
        }
      });
    } catch (error) {
      this.isError = true;
    }
  }
}

export default RulesStore;
